import type {FactoryArg} from 'imask';
import 'imask/masked/date';
import {computed, type Ref} from 'vue';
import type {MaltInputMaskPreset} from '../MaltInputMask.vue';
import {useDateMaskOptions} from './useDateMaskOptions';
import {useNumberMaskOptions} from './useNumberMaskOptions';

interface UseMaskProps {
    preset: Ref<MaltInputMaskPreset | undefined>;
    maskOptions: Ref<Partial<FactoryArg>>;
}

/**
 * Regroup logic about different mask presets options
 *
 * Returns the correct mask options based on the preset
 *
 * @param preset - The preset to use
 * @param maskOptions - The mask options to use
 */
export function useMask({maskOptions, preset}: UseMaskProps) {
    const {dateMaskOptions} = useDateMaskOptions({preset, maskOptions});
    const {numberMaskOptions} = useNumberMaskOptions({preset, maskOptions});

    const resolvedMaskOptions = computed<FactoryArg | null>(() => {
        if (!preset.value) {
            return maskOptions.value;
        } else if (preset.value && ['date', 'date-month-year', 'date-year', 'date-time'].includes(preset.value)) {
            return dateMaskOptions.value;
        } else if (preset.value === 'date-range') {
            return {
                mask: 'date - date',
                blocks: {
                    date: dateMaskOptions.value,
                },
            };
        } else if (['integer', 'price'].includes(preset.value)) {
            return numberMaskOptions.value;
        }
        return null;
    });

    return {
        resolvedMaskOptions,
    };
}
